import Collection from 'ol/Collection';
import Event from 'ol/events/Event';
import Feature from 'ol/Feature';
import GeoJSON from 'ol/format/GeoJSON';
import Point from 'ol/geom/Point';
import { unByKey } from 'ol/Observable';
import VectorEventType from 'ol/source/VectorEventType';

import { logger } from 'cadenza/utils/logging';
import { InteractionGroup } from 'cadenza/map/interactions/interaction-group';
import { GeometryType, getBaseGeometryType, isMultiGeometryType } from 'cadenza/map/geom/geometry-type';
import { SnapInteraction } from 'cadenza/map/interactions/snap-interaction';
import { SelectOverlayInteraction } from 'cadenza/map/interactions/select-overlay-interaction';
import { ajax } from 'cadenza/api-client/api';
import { MapProperty } from 'cadenza/map/map-property';
import { TranslateByHandleInteraction } from 'cadenza/map/interactions/translate-by-handle-interaction';
import { FeatureProperty } from 'cadenza/map/feature-property';
import {
  combineGeometries,
  ClassicUnionWithoutIntersectionStrategy
} from 'cadenza/map/interactions/classic/classic-geometry-edit-strategy/classic-union-without-intersection-strategy';
import {
  clearSourceIgnoringRemoveEvent,
  removeFeatureIgnoringRemoveEvent
} from 'cadenza/map/custom/map-client-edit-geometry/undo-redo-utils';
import { assert } from 'cadenza/utils/custom-error';
import { getSelectStyle } from 'cadenza/map/style/select-styles';
import { ClassicDrawInteraction, IS_FEATURE_VALID, IS_FEATURE_WITH_HOLES } from 'cadenza/map/interactions/classic/classic-draw-interaction';
import { ClassicCadenzaModifyEndEvent, ClassicModifyInteraction } from 'cadenza/map/interactions/classic/classic-modify-interaction';

function cloneFeatureWithoutStyle (feature) {
  // Don't use feature.clone() because we want the cloned feature to have the same geometry, so when it is modified, it is automatically updated in Select and Snap interactions.
  const featureClone = new Feature(feature.getProperties());
  featureClone.setGeometryName(feature.getGeometryName());
  featureClone.setGeometry(feature.getGeometry());
  featureClone.setStyle(null); // feature style overrides layer style (i.e. selected style)
  return featureClone;
}

export const CADENZA_ADD_FEATURE_EVENT = 'cadenza-add-feature';

/**
 * This interaction group combines the geometry drawing and modify interactions,
 * so the user can draw a new geometry and modify it right away without changing the interaction.
 *
 * It is currently used only in Cadenza Web Classic (client/server geometry editing,
 * classical feature editing) and not in Cadenza Workbooks. In Cadenza Workbooks we have
 * separated interaction groups to draw or modify the geometries, see https://conf.disy.net/x/-4rkI.
 *
 * This interaction group can be cleaned up after the "Epic Split" (Workbooks vs Classic).
 */
export class ClassicDrawModifyInteractionGroup extends InteractionGroup {

  #map;
  #isRequestOngoing;
  _snappingLayerId;
  _eventKeys;
  _source;
  _features;
  _type;

  constructor (map, { isRectangle, isCircle, ...olOptions } = {}) {
    const isMultiGeometry = isMultiGeometryType(olOptions.type);
    const style = getGeometryDrawingStyle(olOptions.type, map.get(MapProperty.DRAW_STYLES));
    const type = getExtendedType(isRectangle, isCircle, olOptions.type);
    const drawEndStrategy = new ClassicUnionWithoutIntersectionStrategy();
    const draw = new ClassicDrawInteraction(map, { style, ...olOptions, type, isMultiGeometry, drawEndStrategy });

    // When passing the source instead of a features collection, the Modify interaction clones the
    // features collection of that source and keeps the clone internally only (no public API).
    // in case we pass our cloned features and omit the source (Vector Layer), the Modify interaction
    // creates new Vector Layer and all changes are applied to that layer.
    // In case we want to pass features instead of source we need to listen for change event on that
    // features and remove old and add new feature to our Vector Layer.
    const source = olOptions.source;
    const features = new Collection(source.getFeatures().map(cloneFeatureWithoutStyle));

    const translate = new TranslateByHandleInteraction({ features });

    const modify = new ClassicModifyInteraction({
      ...olOptions,
      withGeometryValidation: true,
      source,
      map
    });
    const select = new SelectOverlayInteraction({
      features, // highlight all edited features (features collection is shared with Modify interaction)
      condition: () => false, // do not handle events, because the interaction is used only for highlighting
      getSelectStyle
    });
    const snap = new SnapInteraction({ features });

    super(draw, translate, modify, select, snap);

    this.#map = map;
    this._source = source;
    this._features = features;
    this._type = olOptions.type;
    this._snappingLayerId = null;
    this.#isRequestOngoing = false;

    this.on('change:active', event => {
      if (!event.oldValue) {
        this._onActivation();
      } else {
        this._onDeactivation();
      }
    });

    this.patchHandleUpEventWithSnapping(draw);
    this.patchHandleUpEventWithSnapping(modify);
  }

  _onActivation () {
    // Because the features collection (used by Modify, Select and Snap interactions) is a live collection,
    // we need to update it by adding new features which we create using the Draw interaction.
    this._eventKeys = registerEventListeners(this._source, this._features, this._type);
    // The features in the source may differ from those in the this._features now,
    // some of them may have been deleted or added by some of the other interactions while this interaction was not active, so sync them.
    // The other solution to this would be to always reinitialize the draw-modify-interaction to make sure the features are in sync
    syncSourceFeaturesToFeatureCollection(this._source.getFeatures(), this._features);
  }

  _onDeactivation () {
    unByKey(this._eventKeys);
  }

  patchHandleUpEventWithSnapping (interaction) {
    const baseHandleUpEvent = interaction.handleUpEvent.bind(interaction);
    interaction.handleUpEvent = event => {
      if (!this.#isRequestOngoing) {
        if (this.snappingLayerId) {
          this.#isRequestOngoing = true;
          this.getSnappingCoord(event.coordinate)
            .then(snappedPoint => {
              event.coordinate[0] = snappedPoint.coordinates[0];
              event.coordinate[1] = snappedPoint.coordinates[1];
              if (interaction.handleDragEvent) { // relevant only for modify interaction
                interaction.handleDragEvent(event);
              }
              baseHandleUpEvent(event);
              if (interaction instanceof ClassicDrawInteraction) {
                interaction.dispatchEventIfFeatureIsPotentiallyDone();
              }
            })
            .catch(err => logger.error('Could\'t obtain snapping point due to ', err))
            .finally(() => { this.#isRequestOngoing = false; });
          return false; // It works because we don't want freehand enabled, so the base code always returns false in this case
        } else {
          const pass = baseHandleUpEvent(event);
          if (interaction instanceof ClassicDrawInteraction) {
            interaction.dispatchEventIfFeatureIsPotentiallyDone();
          }
          return pass;
        }
      }
    };
  }

  get snappingLayerId () {
    return this._snappingLayerId;
  }

  set snappingLayerId (snappingLayerId) {
    this._snappingLayerId = snappingLayerId;
  }

  getSnappingCoord (coordinates) {
    const params = new URLSearchParams();
    const point = new Point(coordinates);
    const srsName = this.#map.getView().getProjection().getCode();
    const pointGeometry = new GeoJSON().writeGeometry(point);
    params.set('to', pointGeometry);
    params.set('resolution', this.#map.getView().getResolution());
    params.set('srsName', srsName);
    return ajax(`/layers/${this.snappingLayerId}/snap?${params.toString()}`);
  }

}

export function getGeometryDrawingStyle (type, stylesMap) {
  const geometryBaseType = getBaseGeometryType(type);
  return stylesMap[geometryBaseType];
}

function getExtendedType (isRectangle, isCircle, geometryType) {
  const rectangleOrDefaultType = isRectangle ? GeometryType.RECTANGLE : geometryType;
  return isCircle ? GeometryType.CIRCLE : rectangleOrDefaultType;
}

function registerEventListeners (source, features, type) {
  return [
    source.on(VectorEventType.ADDFEATURE, ({ feature }) => onFeatureAdded(feature, features, source, type)),
    source.on(VectorEventType.REMOVEFEATURE, event => features.remove(event.feature)),
    source.on(VectorEventType.CLEAR, () => features.clear())
  ];
}

function syncSourceFeaturesToFeatureCollection (sourceFeatures, featuresCollection) {
  const sourceFeaturesSet = new Set(sourceFeatures);
  for (let i = featuresCollection.getLength() - 1; i >= 0; i--) {
    if (!sourceFeaturesSet.has(featuresCollection.item(i))) {
      featuresCollection.removeAt(i);
    }
  }

  const featureCollectionSet = new Set(featuresCollection.getArray());
  for (const sourceFeature of sourceFeatures) {
    if (!featureCollectionSet.has(sourceFeature)) {
      featuresCollection.push(sourceFeature);
    }
  }
}

function onFeatureAdded (newFeature, features, source, type) {
  if (newFeature.get(IS_FEATURE_VALID) === false) {
    removeFeatureIgnoringRemoveEvent(newFeature, source);
    return;
  }
  const sourceFeatures = source.getFeatures();
  if (!isMultiGeometryType(type) && sourceFeatures.length > 1) {
    const firstFeatureClonedGeometry = sourceFeatures[0].getGeometry().clone();
    // Setting the old geometry here allows us to undo to this feature, using the undo/redo mechanism
    newFeature.set(FeatureProperty.OLD_GEOMETRY, firstFeatureClonedGeometry);
    clearSourceIgnoringRemoveEvent(source);
    features.clear();
    // Why we're not adding the feature to the features collection as well?
    // Because when we're adding it to the source, the source will fire an 'addfeature' event so this listener will fire again,
    // since the length is not bigger than 1, the else will execute, adding the feature to the features collection
    source.addFeature(newFeature);
  } else {
    if (type === GeometryType.MULTI_POLYGON && sourceFeatures.length > 1) {
      const multiPolygon = keepAllPolygonsInOneMultiPolygon(newFeature, source);
      // Consider it a modification, not an addition
      source.dispatchEvent(new ClassicCadenzaModifyEndEvent([ multiPolygon ]));
    } else {
      // This is needed too in the case of (type === GeometryType.MULTI_POLYGON && sourceFeatures.length === 1)
      // to be able to undo the creation of a first polygon on a MultiPolygon.
      // (It was previously not being dispatched, if we discover there was some reason for it, please take in mind this use case.)
      /**
       * The issue is the ADDFEATURE event previously used is triggered when we manually add the feature to the source, in the above if.
       * The listener which we have in features-modified-strategy.js was listening to this ADDFEATURE which was triggered too many times.
       * Flags were introduced to ignore one of the event dispatching but this fails when the source is empty, then,
       * we don't even go in the above if, so the flags are not set.
       *
       * Using stopEventListener in the if above doesn't work either because sometimes these listeners are reset and then
       * the listener from features-modified-strategy is triggered first, bypassing our stopEventListener.
       *
       * Instead of this is just easier to map the ADDFEATURE to another event (CadenzaAddFeature) which is triggered only once.
       */
      source.dispatchEvent(new CadenzaAddFeature(newFeature));
      features.push(newFeature);
    }
  }
}

class CadenzaAddFeature extends Event {

  _feature;

  constructor (feature) {
    super(CADENZA_ADD_FEATURE_EVENT);
    this._feature = feature;
  }

  get feature () {
    return this._feature;
  }

}

// For simplicity, and to keep the undo/redo working as it is
function keepAllPolygonsInOneMultiPolygon (newPolygon, source) {
  const sourceFeatures = source.getFeatures();
  assert(sourceFeatures.length === 2, 'This method consolidates the newly added polygon to the existing one, so there should always be 2');
  const existingPolygonFeature = sourceFeatures[0];
  const existingPolygonClonedGeometry = existingPolygonFeature.getGeometry().clone();
  existingPolygonFeature.set(FeatureProperty.OLD_GEOMETRY, existingPolygonClonedGeometry);

  let newGeometry;
  // If the resulting geometry has holes than it contains all the geometries in the source.
  // We do this as a previous step to calculating union-without-intersection
  if (newPolygon.get(IS_FEATURE_WITH_HOLES)) {
    newPolygon.unset(IS_FEATURE_WITH_HOLES);
    newGeometry = newPolygon.getGeometry();
  } else {
    newGeometry = combineGeometries(sourceFeatures.map(f => f.getGeometry()));
  }

  existingPolygonFeature.setGeometry(newGeometry);

  removeFeatureIgnoringRemoveEvent(newPolygon, source);
  return existingPolygonFeature;
}
